import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { find } from 'lodash';
import StarIcon from '@material-ui/icons/Star';
import moment from 'moment';

import { LeaderboardConfigFragment, UserSegmentFragment } from 'api';

interface IProps {
  config: LeaderboardConfigFragment;
  segments: UserSegmentFragment[];
}

const useStyles = makeStyles(() => ({
  dividerHeader: {
    margin: '12px -16px 24px -16px',
  },
  blank: {
    color: 'transparent',
  },
  lowercase: {
    textTransform: 'lowercase',
  },
}));

const View: FC<IProps> = props => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { config, segments } = props;
  const { all_users, description, segment_ids, start_at, end_at } = config;

  function renderSegmentNames() {
    if (all_users) {
      return (
        <Fragment>
          <em>{t('admin:allPeople')}</em>
        </Fragment>
      );
    }

    const segmentList = segment_ids.reduce((list, id) => {
      const segment = find(segments, ['id', id]);
      return list.concat(segment ? [segment.title] : []);
    }, []);

    if (!segmentList.length) {
      return (
        <Fragment>
          <em>{t('admin:leaderboardsSegmentsMissing')}</em>
        </Fragment>
      );
    }

    return segmentList.join(', ');
  }

  function renderDuration() {
    if (all_users) {
      return <em>{t('leaderboard:timeframe')}</em>;
    }

    const startDate = !!start_at && moment.unix(start_at).format('ll');
    const endDate = !!end_at && moment.unix(end_at).format('ll');
    const includeSeparator = !!start_at && !!end_at;

    if (!startDate && !endDate) {
      return <em>{t('admin:leaderboardsDurationMissing')}</em>;
    }

    return (
      <Fragment>
        {startDate}{' '}
        {includeSeparator && <span className={classes.lowercase}>{t('common:to')}</span>} {endDate}
      </Fragment>
    );
  }

  function renderDetail() {
    return (
      <Grid item>
        <Grid container>
          <Grid item xs={6}>
            <Typography variant="subtitle2">{t('common:segment_plural')}</Typography>
            <Typography color="textSecondary">{renderSegmentNames()}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">{t('common:duration')}</Typography>
            <Typography color="textSecondary">{renderDuration()}</Typography>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <Fragment>
      <Divider className={classes.dividerHeader} />
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <StarIcon className={classes.blank} />
        </Grid>
        <Grid item xs>
          <Grid container direction="column" wrap="nowrap" spacing={3}>
            {!!description && (
              <Grid item>
                <Grid container>
                  <Grid item xs>
                    <Typography variant="subtitle2">{t('common:description')}</Typography>
                    <Typography color="textSecondary">{description}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {renderDetail()}
          </Grid>
        </Grid>
        <Grid item>
          <StarIcon className={classes.blank} />
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default View;
